import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { ObjectId } from 'bson';
import { normalizeUserId, extractEntriesWithLabels, reconcileEntries } from '../helpers/helpers';
import {
  fetchEntrySources, createEntrySource, updateEntrySourceAmount, deleteEntrySource,
  fetchPaymentSources, seedPaymentSources
} from '../helpers/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

class NotesStore {
  // User state
  userId = null;
  userName = null;
  isGuest = false;
  authToken = null;

  // Notes state (private observables)
  _notes = [];
  _currentNoteId = null;

  // Metadata cache (private observable)
  _metadata = new Map(); // Map<metadataId, { id, name, kind, ... }>

  // Suggestions (private observable)
  _suggestions = [];

  // Entry sources — which income funded an expense (private observable)
  _entrySources = [];

  // Payment sources — cash / card instruments (private observable)
  _paymentSources = [];

  // UI state (private observables)
  _loading = false;
  _syncStatus = null; // 'synced' | 'syncing' | 'error' | null
  _modalOpen = false;
  _editingDueId = null;
  _selectedIds = new Set();
  _notesSortBy = 'recent'; // 'manual' | 'recent'

  // Debounce timers
  saveTimer = null;
  localStorageTimer = null;

  constructor() {
    makeAutoObservable(this);
    this.restoreSession();
    this.setupHashNavigation();
    this.loadSortPreference();
  }

  loadSortPreference() {
    // Sort preference comes from API for logged-in users
    // Guests use default 'recent' (sorting disabled for guests)
  }

  getAuthHeader() {
    if (this.authToken) {
      return `Bearer ${this.authToken}`;
    }
    // Fallback for dev/guest mode
    if (this.userId?.startsWith('user:') || this.userId?.startsWith('guest:')) {
      return this.userId;
    }
    return `user:${this.userId}`;
  }

  setupHashNavigation() {
    // Listen to hash changes (browser back/forward)
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash;
      if (hash.startsWith('#note-')) {
        const noteId = hash.replace('#note-', '');
        if (noteId && noteId !== this.currentNoteId) {
          // Find note and switch to it
          const note = this.notes.find(n => n.id === noteId);
          if (note) {
            this._currentNoteId = noteId;
            // Load full details if authenticated
            if (!this.isGuest) {
              this.loadNoteDetails(noteId);
            }
          }
        }
      }
    });

    // On initial load, check if there's a hash
    const hash = window.location.hash;
    if (hash.startsWith('#note-')) {
      const noteId = hash.replace('#note-', '');
      if (noteId) {
        // This will be applied after notes are loaded
        this.initialNoteIdFromHash = noteId;
      }
    }
  }

  // ===== Public Getters =====

  get notes() {
    return toJS(this._notes);
  }

  get currentNoteId() {
    return toJS(this._currentNoteId);
  }

  get loading() {
    return toJS(this._loading);
  }

  get syncStatus() {
    return toJS(this._syncStatus);
  }

  get modalOpen() {
    return toJS(this._modalOpen);
  }

  get editingDueId() {
    return toJS(this._editingDueId);
  }

  get selectedIds() {
    return toJS(this._selectedIds);
  }

  get metadata() {
    return toJS(this._metadata);
  }

  get suggestions() {
    return toJS(this._suggestions);
  }

  get entrySources() {
    return toJS(this._entrySources);
  }

  get paymentSources() {
    return toJS(this._paymentSources);
  }

  get notesSortBy() {
    return toJS(this._notesSortBy);
  }

  // ===== Computed Values =====

  get currentNote() {
    const note = this.notes.find(n => n.id === this.currentNoteId);
    // Only warn if we're looking for a specific note AND notes are loaded but note doesn't exist
    if (!note && this.currentNoteId && this.notes.length > 0) {
      console.warn('currentNote getter: note not found', {
        currentNoteId: this.currentNoteId,
        notesCount: this.notes.length,
        noteIds: this.notes.map(n => n.id)
      });
    }
    return note || null;
  }

  get hasNotes() {
    return this.notes.length > 0;
  }

  get storageMode() {
    return this.isGuest ? 'local' : 'api';
  }

  // Reconcile any note's content against its stored entryIndex/payments,
  // dropping ghost entries whose ids no longer match any text in `content`.
  // Used both by the `reconciled` getter (for the currently open note) and
  // by performNoteAPIUpdate (for whichever note is actually being saved,
  // which may not be the currently open one).
  reconcileNote(note) {
    if (!note) {
      return { entries: [], entryIndex: {}, payments: {} };
    }

    const content = note.content || '';
    const payments = note.payments;
    const entryIndex = note.entryIndex;

    if (!content) {
      return { entries: [], entryIndex: {}, payments: {} };
    }

    const raw = extractEntriesWithLabels(content);
    return reconcileEntries(raw, { ...note, payments, entryIndex });
  }

  get reconciled() {
    return this.reconcileNote(this.currentNote);
  }

  get entries() {
    return this.reconciled.entries;
  }

  get entryIndex() {
    return this.reconciled.entryIndex;
  }

  get payments() {
    return this.reconciled.payments;
  }

  get negatives() {
    return this.entries.filter(e => e.value < 0);
  }

  get positives() {
    return this.entries.filter(e => e.value > 0);
  }

  /**
   * Which bucket an entry's money belongs to: 'cash', 'viaCard' or 'reminderOnly'.
   *
   * Mirrors classifyEntry() in api/mcp/normalize.js — the two must agree, or the
   * header and what Claude reports for the same month diverge. The buckets are
   * disjoint: reminderOnly wins over the card check, because it is the stronger
   * claim ("no money moves here") and overlapping buckets would double-count.
   */
  countedIn(entry) {
    if (entry.reminderOnly === true) return 'reminderOnly';
    const source = entry.paymentSourceId ? this.paymentSourceById(entry.paymentSourceId) : null;
    if (source?.kind === 'credit-card') return 'viaCard';
    return 'cash';
  }

  /**
   * An entry settled on a card is not a cash movement this month — the card
   * invoice is, and that invoice is its own line in the notebook. Counting both
   * double-counts, so card and reminder entries stay out of every header figure
   * while remaining fully visible in the table.
   */
  isExcludedFromTotals(entry) {
    return this.countedIn(entry) !== 'cash';
  }

  /** Entries that count towards the cash totals. */
  get cashEntries() {
    return this.entries.filter(e => !this.isExcludedFromTotals(e));
  }

  // The excluded entries are deliberately not sub-totalled here: the header
  // shows cash only, and the per-row chip is what says why a line is missing
  // from it. MCP still reports totals.viaCard / totals.reminderOnly, which is
  // where a breakdown is actually asked for.

  get negativesPaid() {
    return this.cashEntries.filter(e => e.value < 0 && e.is_paid).reduce((a, e) => a + e.value, 0);
  }

  get negativesUnpaid() {
    return this.cashEntries.filter(e => e.value < 0 && !e.is_paid).reduce((a, e) => a + e.value, 0);
  }

  get sum() {
    return this.cashEntries.reduce((a, e) => a + e.value, 0);
  }

  get sumIn() {
    return this.cashEntries.filter(e => e.value > 0).reduce((a, e) => a + e.value, 0);
  }

  get sumOut() {
    return this.cashEntries.filter(e => e.value < 0).reduce((a, e) => a + e.value, 0);
  }

  get selectedSum() {
    if (!this.selectedIds.size) return 0;
    return this.negatives.filter(r => this.selectedIds.has(r.id)).reduce((a, r) => a + Math.abs(r.value), 0);
  }

  get amountStillLeft() {
    return this.sumIn + this.negativesPaid; // negativesPaid is already negative, so adding it subtracts the amount
  }

  // ===== Payment Sources (cash / card instruments) =====

  paymentSourceById(id) {
    if (!id) return null;
    return this.paymentSources.find(s => s.id === id) || null;
  }

  /** Display name for a source id, for history rows whose source may be gone. */
  paymentSourceName(id) {
    return this.paymentSourceById(id)?.name || '(removida)';
  }

  async loadPaymentSources() {
    if (!this.userId || this.isGuest) return;

    try {
      const data = await fetchPaymentSources(this.userId);
      let sources = data.sources || [];

      // First run for this user: the defaults are what make the picker usable,
      // and seeding is idempotent server-side, so do it here rather than making
      // the user find a settings screen.
      if (sources.length === 0) {
        const seeded = await seedPaymentSources(this.userId);
        sources = seeded.sources || [];
      }

      runInAction(() => { this._paymentSources = sources; });
    } catch (err) {
      console.error('Failed to load payment sources:', err);
    }
  }

  /**
   * Point an entry at a payment source, or clear it with null.
   * Discrete action — saved immediately, like the paid checkbox.
   */
  setEntryPaymentSource(entryId, paymentSourceId) {
    this.updatePaymentInfo(entryId, { paymentSourceId: paymentSourceId || null });
  }

  /** Flag an entry as a reminder rather than a real movement. */
  setEntryReminderOnly(entryId, reminderOnly) {
    this.updatePaymentInfo(entryId, { reminderOnly: reminderOnly === true });
  }

  /**
   * Write payment-info fields onto the current note's entry and save.
   *
   * Goes through the normal note PUT rather than a dedicated route, so
   * processEntryHistory() on the server emits the payment_source_changed /
   * reminder_only_changed events the same way it does for paid_at.
   */
  updatePaymentInfo(entryId, fields) {
    const noteId = this.currentNoteId;
    if (!noteId) return;

    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('updatePaymentInfo: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};

    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: { ...(payments[entryId] || {}), ...fields }
      },
      // Kept in step with payments, matching togglePayment().
      entryIndex: this.entryIndex
    };

    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  // ===== Entry Sources (income allocation) =====

  sourcesForExpense(entryId) {
    return this.entrySources.filter(
      s => s.expense.entryId === entryId && s.expense.noteId === this.currentNoteId
    );
  }

  sourcesForIncome(entryId) {
    return this.entrySources.filter(
      s => s.income.entryId === entryId && s.income.noteId === this.currentNoteId
    );
  }

  allocatedForExpense(entryId) {
    return this.sourcesForExpense(entryId).reduce((a, s) => a + s.amount, 0);
  }

  allocatedForIncome(entryId) {
    return this.sourcesForIncome(entryId).reduce((a, s) => a + s.amount, 0);
  }

  async loadEntrySources(noteId) {
    if (!this.userId || this.isGuest || !noteId) return;

    try {
      const data = await fetchEntrySources(this.userId, noteId);
      runInAction(() => { this._entrySources = data.sources || []; });
    } catch (err) {
      console.error('Failed to load entry sources:', err);
    }
  }

  async addEntrySource({ expenseNoteId, expenseEntryId, incomeNoteId, incomeEntryId, amount }) {
    if (!this.userId || this.isGuest) return;

    try {
      await createEntrySource(this.userId, { expenseNoteId, expenseEntryId, incomeNoteId, incomeEntryId, amount });
      await this.loadEntrySources(this.currentNoteId);
    } catch (err) {
      console.error('Failed to add entry source:', err);
    }
  }

  async changeEntrySourceAmount(id, amount) {
    if (!this.userId || this.isGuest) return;

    try {
      await updateEntrySourceAmount(this.userId, id, amount);
      await this.loadEntrySources(this.currentNoteId);
    } catch (err) {
      console.error('Failed to update entry source amount:', err);
    }
  }

  async removeEntrySource(id) {
    if (!this.userId || this.isGuest) return;

    try {
      await deleteEntrySource(this.userId, id);
      await this.loadEntrySources(this.currentNoteId);
    } catch (err) {
      console.error('Failed to remove entry source:', err);
    }
  }

  // ===== Metadata Management =====

  /**
   * Get metadata name by ID, returns null if not found
   */
  getMetadataName(metadataId) {
    if (!metadataId) return null;
    const meta = this._metadata.get(metadataId);
    return meta?.name || null;
  }

  /**
   * Get full metadata object by ID
   */
  getMetadata(metadataId) {
    if (!metadataId) return null;
    return this._metadata.get(metadataId) || null;
  }

  /**
   * Load all user metadata from API
   */
  async loadMetadata() {
    if (!this.userId || this.isGuest) return;

    const authHeader = this.getAuthHeader();

    try {
      const res = await fetch(`${API_URL}/api/financial-entries-metadata`, {
        headers: {
          'Authorization': authHeader
        }
      });

      if (!res.ok) {
        console.error('loadMetadata: failed with status', res.status);
        return;
      }

      const data = await res.json();
      const metadataList = data.metadata || [];

      // Store in Map for O(1) lookup
      runInAction(() => {
        this._metadata.clear();
        for (const meta of metadataList) {
          this._metadata.set(meta.id, meta);
        }
      });
    } catch (err) {
      console.error('Failed to load metadata:', err);
    }
  }

  /**
   * Load entries across all notes that have a specific metadataId
   * @param {string} metadataId - The metadata ID to search for
   * @returns {Promise<{entries: Array, metadata: Object}|null>}
   */
  async loadEntriesByMetadata(metadataId) {
    if (!this.userId || this.isGuest || !metadataId) return null;

    const authHeader = this.getAuthHeader();

    try {
      const res = await fetch(`${API_URL}/api/entries-by-metadata/${metadataId}`, {
        headers: {
          'Authorization': authHeader
        }
      });

      if (!res.ok) {
        console.error('loadEntriesByMetadata: failed with status', res.status);
        return null;
      }

      return await res.json();
    } catch (err) {
      console.error('Failed to load entries by metadata:', err);
      return null;
    }
  }

  /**
   * Load recurring expense suggestions for a note based on previous month's data
   * @param {string} noteId - The note ID to fetch suggestions for
   */
  async loadSuggestions(noteId) {
    if (!this.userId || this.isGuest || !noteId) return;

    try {
      const res = await fetch(`${API_URL}/api/notes/${noteId}/suggestions`, {
        headers: { 'Authorization': this.getAuthHeader() }
      });
      if (!res.ok) return;
      const data = await res.json();
      runInAction(() => { this._suggestions = data.suggestions || []; });
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  }

  // ===== User Management =====

  restoreSession() {
    try {
      const storedId = localStorage.getItem('userId');
      const storedToken = localStorage.getItem('authToken');

      if (storedId) {
        const normalizedId = normalizeUserId(storedId);
        this.userId = normalizedId;
        this.authToken = storedToken || null;
        this.isGuest = normalizedId?.startsWith('guest:');
        this.loadNotes();
      }
    } catch (err) {
      console.error('Failed to load userId:', err);
    }
  }

  async startSession(userId, metadata, token = null) {
    const normalizedId = normalizeUserId(userId);

    this.userId = normalizedId;
    this.authToken = token;
    this.isGuest = normalizedId?.startsWith('guest:');

    if (normalizedId) {
      localStorage.setItem('userId', normalizedId);
      if (token) {
        localStorage.setItem('authToken', token);
      }

      // Save user metadata to API if provided (from Apple login)
      if (metadata && !this.isGuest) {
        await this.saveUserMetadata(metadata);
      }

      this.loadNotes();
    }
  }

  logout() {
    this.userId = null;
    this.userName = null;
    this.isGuest = false;
    this.authToken = null;
    this._notes = [];
    this._currentNoteId = null;
    localStorage.removeItem('userId');
    localStorage.removeItem('authToken');
    if (this.isGuest) {
      localStorage.removeItem('notes');
    }
  }

  // ===== Notes Loading =====

  async loadNotes() {
    if (!this.userId) {
      console.warn('loadNotes: no userId');
      return;
    }

    this._loading = true;

    try {
      let loadedNotes = [];

      if (this.isGuest) {
        // Load from localStorage for guests
        loadedNotes = this.loadFromLocalStorage();
      } else {
        // Load from API for authenticated users
        loadedNotes = await this.loadFromAPI();
      }

      runInAction(() => {
        this._notes = loadedNotes;

        // Ensure currentNoteId points to a valid note
        if (loadedNotes.length > 0) {
          // Check if there was a hash on initial load
          if (this.initialNoteIdFromHash) {
            const noteFromHash = loadedNotes.find(n => n.id === this.initialNoteIdFromHash);
            if (noteFromHash) {
              this._currentNoteId = this.initialNoteIdFromHash;
              this.initialNoteIdFromHash = null; // Clear after use
            } else {
              this._currentNoteId = loadedNotes[0].id;
              this.updateHash(loadedNotes[0].id);
            }
          } else {
            const currentExists = loadedNotes.some(n => n.id === this.currentNoteId);
            if (!currentExists) {
              this._currentNoteId = loadedNotes[0].id;
              this.updateHash(loadedNotes[0].id);
            } else if (this.currentNoteId) {
              this.updateHash(this.currentNoteId);
            }
          }
        } else {
          this._currentNoteId = null;
          // Create a default note if none exist
          this.addNote();
          return; // addNote will trigger save
        }

        this._syncStatus = this.isGuest ? null : 'synced';
      });

      // Step 2: For API users, fetch full details of current note
      if (!this.isGuest && this.currentNoteId) {
        await this.loadNoteDetails(this.currentNoteId);
      }
    } catch (err) {
      console.error('Failed to load notes:', err);
      runInAction(() => {
        this._syncStatus = 'error';
      });
    } finally {
      runInAction(() => {
        this._loading = false;
      });
    }
  }

  loadFromLocalStorage() {
    try {
      const data = localStorage.getItem('notes');
      return data ? JSON.parse(data) : [];
    } catch (err) {
      console.error('Failed to load from localStorage:', err);
      return [];
    }
  }

  async loadFromAPI() {
    const authHeader = this.getAuthHeader();

    // Fetch notes - API uses user's saved sort preference by default
    const res = await fetch(`${API_URL}/api/notes`, {
      headers: {
        'Authorization': authHeader
      }
    });

    if (!res.ok) {
      console.error('loadFromAPI: failed with status', res.status);
      if (res.status === 401) {
        // Missing, expired or no-longer-accepted credentials: drop the session
        // so App renders the login screen instead of a dead, empty notebook.
        this.logout();
      }
      throw new Error(`Failed to load notes: ${res.status}`);
    }

    const data = await res.json();

    // Extract and store user metadata
    runInAction(() => {
      if (data.userName) {
        this.userName = data.userName;
      } else if (data.userEmail) {
        this.userName = data.userEmail.split('@')[0];
      }
      // Sync sort preference from server
      if (data.notesSortBy) {
        this._notesSortBy = data.notesSortBy;
      }
    });

    return data.notes || [];
  }

  // Fetch full details for a specific note
  async loadNoteDetails(noteId) {
    if (!noteId) return;

    const authHeader = this.getAuthHeader();

    try {
      // Load note details and metadata in parallel
      const [noteRes] = await Promise.all([
        fetch(`${API_URL}/api/notes/${noteId}`, {
          headers: {
            'Authorization': authHeader
          }
        }),
        this.loadMetadata() // Load metadata alongside note details
      ]);

      if (!noteRes.ok) {
        console.error('loadNoteDetails: failed with status', noteRes.status);
        return;
      }

      const fullNote = await noteRes.json();

      // Update the note in the list with full details
      runInAction(() => {
        const noteIndex = this._notes.findIndex(n => n.id === noteId);
        if (noteIndex !== -1) {
          this._notes[noteIndex] = fullNote;
        }
      });

      // Load suggestions, entry sources and payment sources after note is in store
      await Promise.all([
        this.loadSuggestions(noteId),
        this.loadEntrySources(noteId),
        // Not note-scoped, but cheap and needed before the table can render a
        // card chip; re-fetching keeps a source created in another tab visible.
        this.loadPaymentSources()
      ]);
    } catch (err) {
      console.error('Failed to load note details:', err);
    }
  }

  async saveUserMetadata(metadata) {
    const authHeader = this.getAuthHeader();

    try {
      const res = await fetch(`${API_URL}/api/user/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(metadata)
      });

      if (!res.ok) {
        console.error('saveUserMetadata: failed with status', res.status);
        throw new Error(`Failed to save user metadata: ${res.status}`);
      }

      // Update local userName after successful save
      if (metadata.name) {
        runInAction(() => {
          this.userName = metadata.name;
        });
      }
    } catch (err) {
      console.error('Failed to save user metadata:', err);
      // Don't throw - metadata save is not critical
    }
  }

  // ===== Notes Saving =====

  // Save all notes to localStorage immediately (for guests)
  saveToLocalStorageNow() {
    try {
      // Build notes array with reconciled data for current note
      const notesToSave = this.notes.map(note => {
        if (note.id === this.currentNoteId) {
          return {
            ...note,
            payments: this.payments,
            entryIndex: this.entryIndex
          };
        }
        return note;
      });

      localStorage.setItem('notes', JSON.stringify(notesToSave));
    } catch (err) {
      console.error('Failed to save to localStorage:', err);
    }
  }

  // Debounced save to localStorage (for guests)
  saveToLocalStorage(immediate = false) {
    if (immediate) {
      this.saveToLocalStorageNow();
      return;
    }

    // Clear any pending save
    if (this.localStorageTimer) {
      clearTimeout(this.localStorageTimer);
    }

    // Debounced save
    this.localStorageTimer = setTimeout(() => {
      this.saveToLocalStorageNow();
    }, 1000);
  }

  // Save a specific note to API (for regular users)
  async saveNoteToAPI(noteId, immediate = false) {
    if (!this.userId || this.isGuest) return;

    const note = this.notes.find(n => n.id === noteId);
    if (!note) {
      console.warn('saveNoteToAPI: note not found', noteId);
      return;
    }

    // Debounce API saves (except immediate)
    if (!immediate) {
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.performNoteAPIUpdate(noteId), 1000);
      return;
    }

    await this.performNoteAPIUpdate(noteId);
  }

  async performNoteAPIUpdate(noteId) {
    const note = this.notes.find(n => n.id === noteId);
    if (!note) return;

    // Always reconcile against this specific note's own content, regardless
    // of whether it's the currently open note — the user may have switched
    // notes while this save was debounced, and the in-memory note.payments/
    // note.entryIndex are not otherwise kept in sync with content edits.
    const { payments: paymentsToSave, entryIndex: entryIndexToSave } = this.reconcileNote(note);

    this._syncStatus = 'syncing';

    const authHeader = this.getAuthHeader();

    try {
      const res = await fetch(`${API_URL}/api/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          title: note.title,
          content: note.content,
          payments: paymentsToSave,
          entryIndex: entryIndexToSave,
          isSampleData: note.isSampleData
        })
      });

      if (!res.ok) {
        throw new Error(`Failed to save note: ${res.status}`);
      }

      runInAction(() => {
        this._syncStatus = 'synced';
      });
    } catch (err) {
      console.error('Failed to save note to API:', err);
      runInAction(() => {
        this._syncStatus = 'error';
      });
    }
  }

  // Create a new note on API (for regular users)
  async createNoteOnAPI(note) {
    if (!this.userId || this.isGuest) return;

    this._syncStatus = 'syncing';

    const authHeader = this.getAuthHeader();

    try {
      // Send minimal data - let backend generate title
      const payload = {
        id: note.id,
        content: note.content,
        payments: note.payments,
        entryIndex: note.entryIndex,
        isSampleData: note.isSampleData
      };

      const res = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Failed to create note: ${res.status}`);
      }

      const result = await res.json();

      // Update local note with backend-generated title
      if (result.note && result.note.title) {
        runInAction(() => {
          const noteIndex = this._notes.findIndex(n => n.id === note.id);
          if (noteIndex !== -1) {
            this._notes[noteIndex] = {
              ...this._notes[noteIndex],
              title: result.note.title
            };
          }
          this._syncStatus = 'synced';
        });
      } else {
        runInAction(() => {
          this._syncStatus = 'synced';
        });
      }
    } catch (err) {
      console.error('Failed to create note on API:', err);
      runInAction(() => {
        this._syncStatus = 'error';
      });
    }
  }

  // Delete a note from API (for regular users)
  async deleteNoteFromAPI(noteId) {
    if (!this.userId || this.isGuest) return;

    const authHeader = this.getAuthHeader();

    try {
      const res = await fetch(`${API_URL}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to delete note: ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to delete note from API:', err);
      throw err;
    }
  }

  // ===== Note Operations =====

  addNote() {
    const now = new Date();
    const id = new ObjectId().toString();

    // Check if this is the first note (sample data for new users)
    const isFirstNote = this.notes.length === 0;

    let title;

    // For guest users, generate title client-side
    if (this.isGuest) {
      // Generate month name in Portuguese with year
      const monthNames = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      const shortMonthNames = [
        'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
        'jul', 'ago', 'set', 'out', 'nov', 'dez'
      ];

      let month = now.getMonth();
      let year = now.getFullYear();

      // Helper to check if a month/year combination exists
      const monthExists = (m, y) => {
        const pattern = new RegExp(
          `(${monthNames[m]}|${shortMonthNames[m]}).*${y}`,
          'i'
        );
        return this.notes.some(n => pattern.test(n.title));
      };

      // Loop until we find an available month (max 24 iterations to prevent infinite loop)
      let iterations = 0;
      while (monthExists(month, year) && iterations < 24) {
        month++;
        if (month > 11) {
          month = 0;
          year++;
        }
        iterations++;
      }

      title = `${monthNames[month]} - ${year}`;
    } else {
      // For authenticated users, use temporary title that will be replaced by backend
      title = 'Carregando...';
    }

    const note = {
      id,
      title,
      content: isFirstNote ? 'valor 100\n-10 compra item x\nfatura -40 uber -20' : '',
      payments: {},
      entryIndex: {},
      isSampleData: isFirstNote // Flag to populate sample payments after reconciliation
    };

    this._notes = [note, ...this.notes];
    this._currentNoteId = id;
    this._modalOpen = false;
    this._suggestions = []; // Clear stale suggestions from the previously open note
    this._entrySources = []; // Clear stale entry sources from the previously open note
    this.updateHash(id);

    // Save immediately
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.createNoteOnAPI(note).then(() => this.loadSuggestions(id));
    }
  }

  updateNote(noteId, updates) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('updateNote: note not found', noteId);
      return;
    }

    this._notes[noteIndex] = {
      ...this.notes[noteIndex],
      ...updates
    };

    // Debounced save
    if (this.isGuest) {
      this.saveToLocalStorage(false);
    } else {
      this.saveNoteToAPI(noteId, false);
    }
  }

  updateCurrentNote(updates) {
    if (!this.currentNoteId) return;
    this.updateNote(this.currentNoteId, updates);
  }

  async deleteNote(noteId) {
    // Delete from API first if not guest
    if (!this.isGuest) {
      try {
        await this.deleteNoteFromAPI(noteId);
      } catch (err) {
        console.error('Failed to delete note from API, continuing with local delete');
      }
    }

    this._notes = this.notes.filter(n => n.id !== noteId);

    if (this.currentNoteId === noteId) {
      this._currentNoteId = this.notes.length > 0 ? this.notes[0].id : null;
    }

    // Save immediately for guests
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    }
  }

  renameNote(noteId, newTitle) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) return;

    // Replace the entire note to trigger MobX reaction
    this._notes[noteIndex] = {
      ...this.notes[noteIndex],
      title: newTitle
    };

    // Save immediately
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  moveNote(noteId, direction) {
    // Create a copy and swap
    const notesCopy = [...this._notes];

    const fromIndex = notesCopy.findIndex(n => n.id === noteId);
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;

    if (fromIndex === -1 || toIndex < 0 || toIndex >= notesCopy.length) return;

    [notesCopy[fromIndex], notesCopy[toIndex]] = [notesCopy[toIndex], notesCopy[fromIndex]];

    // Assign order values to all notes based on their new positions
    notesCopy.forEach((note, idx) => {
      notesCopy[idx] = {
        ...note,
        order: idx
      };
    });

    this._notes = notesCopy;

    // Save immediately
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      // For API users, save the reordered notes
      this.saveNotesOrder();
    }
  }

  async saveNotesOrder() {
    if (this.isGuest) return;

    try {
      const authHeader = this.getAuthHeader();

      // Send only id and order for each note
      const notesOrder = this.notes.map(note => ({
        id: note.id,
        order: note.order
      }));

      const res = await fetch(`${API_URL}/api/notes/reorder`, {
        method: 'PATCH',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: notesOrder })
      });

      if (!res.ok) {
        throw new Error(`Failed to reorder notes: ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to save notes order:', err);
    }
  }

  // ===== Payment Management =====

  togglePayment(noteId, entryId) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('togglePayment: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || {};
    const newIsPaid = !prev.is_paid;

    // Get current entryIndex from reconciliation (only valid for current note)
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});

    // Replace the entire note to trigger MobX reaction
    // Update both payments AND entryIndex to maintain consistency
    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: {
          ...prev,
          is_paid: newIsPaid,
          // Recorded automatically on check, cleared on uncheck so an unpaid entry
          // never carries a stale timestamp.
          paid_at: newIsPaid ? new Date().toISOString() : null
        }
      },
      entryIndex: currentEntryIndex
    };

    // Immediate save for checkbox
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  async updateDueDate(noteId, entryId, dueDate) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('updateDueDate: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || {};

    // Get current entryIndex from reconciliation (only valid for current note)
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});

    // Replace the entire note to trigger MobX reaction
    // Update both payments AND entryIndex to maintain consistency
    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: {
          ...prev,
          due_date: dueDate
        }
      },
      entryIndex: currentEntryIndex
    };

    // Immediate save for date change
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      await this.saveNoteToAPI(noteId, true);
    }
  }

  async updatePaidAt(noteId, entryId, isoOrNull) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('updatePaidAt: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || {};

    // Get current entryIndex from reconciliation (only valid for current note)
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});

    // Replace the entire note to trigger MobX reaction
    // Update both payments AND entryIndex to maintain consistency
    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: {
          ...prev,
          paid_at: isoOrNull
        }
      },
      entryIndex: currentEntryIndex
    };

    // Immediate save for date change
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      await this.saveNoteToAPI(noteId, true);
    }
  }

  // ===== Attachment Management =====

  addAttachment(noteId, entryId, attachmentData) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('addAttachment: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || { is_paid: false, due_date: '' };
    const existingAttachments = prev.attachments || [];

    // Get current entryIndex from reconciliation
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});

    // Replace the entire note to trigger MobX reaction
    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: {
          ...prev,
          attachments: [...existingAttachments, attachmentData]
        }
      },
      entryIndex: currentEntryIndex
    };

    // Immediate save
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  removeAttachment(noteId, entryId, s3Key) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      console.warn('removeAttachment: note not found', noteId);
      return;
    }

    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || { is_paid: false, due_date: '' };
    const existingAttachments = prev.attachments || [];

    // Filter out the attachment with the matching s3Key
    const updatedAttachments = existingAttachments.filter(att => att.s3Key !== s3Key);

    // Get current entryIndex from reconciliation
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});

    // Replace the entire note to trigger MobX reaction
    this._notes[noteIndex] = {
      ...note,
      payments: {
        ...payments,
        [entryId]: {
          ...prev,
          attachments: updatedAttachments
        }
      },
      entryIndex: currentEntryIndex
    };

    // Immediate save
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  updateAttachmentLabel(noteId, entryId, s3Key, displayName) {
    const noteIndex = this.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) return;
    const note = this.notes[noteIndex];
    const payments = note.payments || {};
    const prev = payments[entryId] || {};
    const updatedAttachments = (prev.attachments || []).map(att =>
      att.s3Key === s3Key ? { ...att, displayName: displayName || null } : att
    );
    const currentEntryIndex = noteId === this.currentNoteId ? this.entryIndex : (note.entryIndex || {});
    this._notes[noteIndex] = {
      ...note,
      payments: { ...payments, [entryId]: { ...prev, attachments: updatedAttachments } },
      entryIndex: currentEntryIndex
    };
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      this.saveNoteToAPI(noteId, true);
    }
  }

  // ===== UI State =====

  updateHash(noteId) {
    // Don't update hash when viewing shared notes
    if (window.location.pathname.startsWith('/shared/')) {
      return;
    }
    if (noteId) {
      window.history.replaceState(null, '', `#note-${noteId}`);
    } else {
      window.history.replaceState(null, '', '#');
    }
  }

  setCurrentNoteId(id) {
    this._currentNoteId = id;
    this._suggestions = []; // Clear stale suggestions while loading
    this._entrySources = []; // Clear stale entry sources while loading
    this.updateHash(id);

    // Fetch full details for API users when switching notes
    if (!this.isGuest && id) {
      this.loadNoteDetails(id);
    }
  }

  setModalOpen(isOpen) {
    this._modalOpen = isOpen;
  }

  setEditingDueId(id) {
    this._editingDueId = id;
  }

  async setNotesSortBy(mode) {
    if (this.isGuest) return; // Sorting disabled for guests

    if (mode === 'manual' || mode === 'recent') {
      this._notesSortBy = mode;
      // Persist to server and reload notes
      await this.saveUserMetadata({ notesSortBy: mode });
      const notes = await this.loadFromAPI();
      runInAction(() => {
        this._notes = notes;
      });
    }
  }

  toggleSelection(id) {
    // Replace the Set to trigger MobX reaction
    const newSet = new Set(this.selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    this._selectedIds = newSet;
  }

  clearSelection() {
    this._selectedIds = new Set();
  }

  setSelection(idsSet) {
    this._selectedIds = new Set(idsSet);
  }

  // ===== Convenience Methods for Current Note =====

  togglePaid(row) {
    if (!this.currentNoteId) return;
    this.togglePayment(this.currentNoteId, row.id);
  }

  async changeDueDate(row, iso) {
    if (!this.currentNoteId) return;
    const noteId = this.currentNoteId;
    await this.updateDueDate(noteId, row.id, iso);

    // Refresh suggestions so "already added" rows drop off once their date matches
    if (!this.isGuest) {
      await this.loadSuggestions(noteId);
    }
  }

  async changePaidAt(row, iso) {
    if (!this.currentNoteId) return;
    await this.updatePaidAt(this.currentNoteId, row.id, iso);
  }

  /**
   * Add an entry from a suggestion (previous month recurring expense)
   * Appends a new line to the content, links metadata, and sets due date
   */
  async addSuggestionEntry(suggestion) {
    if (!this.currentNoteId) return;

    const noteIndex = this._notes.findIndex(n => n.id === this.currentNoteId);
    if (noteIndex === -1) return;

    const note = this._notes[noteIndex];

    // Format value for text (sign follows suggestion.kind)
    const absValue = Math.abs(suggestion.lastValue);
    const isIncome = suggestion.kind === 'income';
    const sign = isIncome ? '+' : '-';
    let valueStr;
    if (Number.isInteger(absValue)) {
      valueStr = `${sign}${absValue}`;
    } else {
      valueStr = `${sign}${absValue.toFixed(2).replace('.', ',')}`;
    }

    const newLine = `${suggestion.metadataName} ${valueStr}`;

    // Append after last line, trimming trailing newlines to avoid gaps
    const content = note.content || '';
    const trimmed = content.replace(/\n+$/, '');
    const newContent = trimmed ? trimmed + '\n' + newLine : newLine;

    // Reconcile with new content to find the new entry's ID
    const raw = extractEntriesWithLabels(newContent);
    const reconciled = reconcileEntries(raw, {
      ...note,
      content: newContent,
      payments: note.payments || {},
      entryIndex: note.entryIndex || {}
    });

    // Find entry on the newly appended line
    const lastLineIndex = newContent.split('\n').length - 1;
    const lineEntries = reconciled.entries.filter(e => e.lineIndex === lastLineIndex);
    // A metadata name containing a bare number (e.g. "Salário 2") can make the parser split
    // the line into two entries. Prefer the one matching the intended value over the first
    // match on the line, so metadata/due-date land on the real amount, not a label fragment.
    const expectedValue = isIncome ? absValue : -absValue;
    const newEntry = lineEntries.find(e => e.value === expectedValue) || lineEntries[0];

    const entryIndex = { ...reconciled.entryIndex };
    const payments = { ...reconciled.payments };

    if (newEntry) {
      // Link metadata
      if (entryIndex[newEntry.id]) {
        entryIndex[newEntry.id] = {
          ...entryIndex[newEntry.id],
          metadataId: suggestion.metadataId,
          kind: isIncome ? 'income' : 'expense'
        };
      }

      // Set due date only when the suggestion carries one — income rarely does, and
      // writing '' over an existing value is pointless churn.
      if (suggestion.suggestedDueDate) {
        payments[newEntry.id] = {
          ...(payments[newEntry.id] || {}),
          due_date: suggestion.suggestedDueDate
        };
      }
    }

    // Update note in one shot
    this._notes[noteIndex] = {
      ...note,
      content: newContent,
      entryIndex,
      payments
    };

    // Save immediately and refresh suggestions
    if (this.isGuest) {
      this.saveToLocalStorage(true);
    } else {
      await this.performNoteAPIUpdate(this.currentNoteId);
      await this.loadSuggestions(this.currentNoteId);
    }
  }

  onRowClick(row, evt) {
    const tag = evt.target.tagName;
    if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'A') return;
    this.toggleSelection(row.id);
  }

  // ===== Guest to User Migration =====

  async migrateGuestData(token, userId) {
    if (!this.isGuest) return { ok: false, error: 'Not a guest user' };

    const guestNotes = this.notes;
    if (guestNotes.length === 0) {
      // No data to migrate, just switch user
      await this.startSession(userId, null, token);
      return { ok: true, mergedCount: 0 };
    }

    try {
      const res = await fetch(`${API_URL}/api/notes/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ guestNotes })
      });

      if (!res.ok) {
        throw new Error(`Failed to sync notes: ${res.status}`);
      }

      const result = await res.json();

      // Clear guest localStorage
      localStorage.removeItem('notes');

      // Switch to authenticated user
      await this.startSession(userId, null, token);

      return result;
    } catch (err) {
      console.error('Failed to migrate guest data:', err);
      return { ok: false, error: err.message };
    }
  }

  // ===== SHARE METHODS =====

  /**
   * Create a new share for a note
   * @param {string} noteId - Note ID
   * @param {Object} options - { shareType, permission, email }
   * @returns {Promise<Object>} Created share
   */
  async createNoteShare(noteId, options) {
    if (!this.userId || this.isGuest) {
      throw new Error('Must be logged in to share notes');
    }

    const res = await fetch(`${API_URL}/api/notes/${noteId}/shares`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.getAuthHeader()
      },
      body: JSON.stringify(options)
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to create share');
    }

    return await res.json();
  }

  /**
   * List all shares for a note
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} { shares: [...] }
   */
  async listNoteShares(noteId) {
    if (!this.userId || this.isGuest) {
      throw new Error('Must be logged in to list shares');
    }

    const res = await fetch(`${API_URL}/api/notes/${noteId}/shares`, {
      headers: {
        'Authorization': this.getAuthHeader()
      }
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to list shares');
    }

    return await res.json();
  }

  /**
   * Update a share
   * @param {string} noteId - Note ID
   * @param {string} shareId - Share ID
   * @param {Object} updates - { linkEnabled, permission }
   * @returns {Promise<Object>} Updated share
   */
  async updateNoteShare(noteId, shareId, updates) {
    if (!this.userId || this.isGuest) {
      throw new Error('Must be logged in to update shares');
    }

    const res = await fetch(`${API_URL}/api/notes/${noteId}/shares/${shareId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.getAuthHeader()
      },
      body: JSON.stringify(updates)
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update share');
    }

    return await res.json();
  }

  /**
   * Delete a share
   * @param {string} noteId - Note ID
   * @param {string} shareId - Share ID
   * @returns {Promise<Object>} { ok: true }
   */
  async deleteNoteShare(noteId, shareId) {
    if (!this.userId || this.isGuest) {
      throw new Error('Must be logged in to delete shares');
    }

    const res = await fetch(`${API_URL}/api/notes/${noteId}/shares/${shareId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': this.getAuthHeader()
      }
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete share');
    }

    return await res.json();
  }

  /**
   * Get notes shared with current user
   * @returns {Promise<Object>} { notes: [...] }
   */
  async getNotesSharedWithMe() {
    if (!this.userId || this.isGuest) {
      throw new Error('Must be logged in to view shared notes');
    }

    const res = await fetch(`${API_URL}/api/notes/shared-with-me`, {
      headers: {
        'Authorization': this.getAuthHeader()
      }
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to get shared notes');
    }

    return await res.json();
  }

  async createEntryShare(noteId, options) {
    if (!this.userId || this.isGuest) throw new Error('Must be logged in to share entries');
    const res = await fetch(`${API_URL}/api/entry-shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
      body: JSON.stringify({ noteId, ...options })
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to create entry share'); }
    return await res.json();
  }

  async listEntryShares(noteId) {
    if (!this.userId || this.isGuest) throw new Error('Must be logged in');
    const res = await fetch(`${API_URL}/api/entry-shares?noteId=${noteId}`, {
      headers: { 'Authorization': this.getAuthHeader() }
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to list entry shares'); }
    return await res.json();
  }

  async updateEntryShare(shareId, updates) {
    if (!this.userId || this.isGuest) throw new Error('Must be logged in');
    const res = await fetch(`${API_URL}/api/entry-shares/${shareId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
      body: JSON.stringify(updates)
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to update entry share'); }
    return await res.json();
  }

  async deleteEntryShare(shareId) {
    if (!this.userId || this.isGuest) throw new Error('Must be logged in');
    const res = await fetch(`${API_URL}/api/entry-shares/${shareId}`, {
      method: 'DELETE',
      headers: { 'Authorization': this.getAuthHeader() }
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to delete entry share'); }
    return await res.json();
  }

  async autoAddAttachmentToShares(noteId, entryId) {
    if (!this.userId || this.isGuest) return;
    try {
      const { shares } = await this.listEntryShares(noteId);
      const now = new Date();
      const eligible = shares.filter(s =>
        s.autoAddNewEntries &&
        (!s.expiresAt || new Date(s.expiresAt) > now) &&
        !(s.entries || []).some(e => e.entryId === entryId)
      );
      for (const share of eligible) {
        await this.updateEntryShare(share.id, { addEntry: { entryId } });
      }
    } catch (e) {
      console.error('autoAddAttachmentToShares failed:', e);
    }
  }
}

// Create singleton instance
const notesStore = new NotesStore();

export default notesStore;
